import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ChevronDown, ChevronRight, Drumstick, Heart, MapPin, Mail, RotateCcw, Search, ShieldCheck, ShoppingCart, Snowflake, Star, Truck, User, X, ChevronUp, Sparkles } from "lucide-react";
import { toast } from "sonner";
import StoreLogo from "@/components/storefront/StoreLogo";
import ToneImg from "@/components/storefront/ToneImg";
import MobileNav from "@/components/storefront/MobileNav";
import { Reveal } from "@/components/pharmacy/hooks";
import { socialItems } from "@/lib/social";
import { inr, rating, reviews } from "@/lib/storeutils";
import { discountPct } from "@/lib/homesets";
import { navLinks, footerColumns, footerLines, isNavActive } from "@/lib/storenav";

// Look-and-feel tokens of the "MeatVerse" layout (fresh meat / seafood / eggs): navy hero + footer, red primary, green add-to-cart.
export const M = {
  navy: "#0b1f3f", ink: "#0f1f3d", muted: "#64748b", line: "#e7ebf3", soft: "#f4f7fb",
  red: "var(--mv-primary, #e11d2e)", green: "var(--mv-accent, #1fa35a)", greenSoft: "#e6f6ed", pink: "#fdecee", script: "'Kaushan Script', 'Brush Script MT', cursive",
};
export const needsOptions = (p) => p.variants?.length > 0 || p.variant_items?.length > 0;
export const imgOf = (p) => p?.image || (p?.images || [])[0] || "";

/** "500 g" / "1.2 kg" from the product's weight (kg), else its first pack option, else the category. */
export function packText(p) {
  if (p.weight > 0) return p.weight < 1 ? `${Math.round(p.weight * 1000)} g` : `${+p.weight.toFixed(2)} kg`;
  const opt = p.option_types?.[0]?.values?.[0] || p.variants?.[0]?.options?.[0];
  return opt || p.category || "";
}

const BADGE_COLORS = ["#e11d2e", "#0b1f3f", "#1fa35a", "#0e7490", "#b45309"];
/** The little label on a product card: BESTSELLER (flagged), n% OFF, FRESH (added in the last 3 weeks), else the category. */
export function badgeOf(p, k = 0) {
  const off = discountPct(p);
  let label;
  if (p.featured) label = "BESTSELLER";
  else if (off >= 5) label = `${off}% OFF`;
  else if (p.created_at && Date.now() - new Date(p.created_at).getTime() < 21 * 864e5) label = "FRESH";
  else label = (p.category || "FRESH").toUpperCase().slice(0, 14);
  return { label, color: BADGE_COLORS[(label.length + k) % BADGE_COLORS.length] };
}

export function priceText(p) {
  const prices = (p.variant_items || []).map((v) => v.price).filter((n) => n > 0);
  if (prices.length > 1) { const lo = Math.min(...prices), hi = Math.max(...prices); if (lo !== hi) return `${inr(lo)} – ${inr(hi)}`; }
  return inr(prices.length ? prices[0] : p.price);
}

export function Stars({ id, size = 11, className = "" }) {
  const r = rating(id);
  return (
    <span className={`inline-flex items-center gap-1 ${className}`} aria-label={`Rated ${r.toFixed(1)} out of 5`}>
      <Star size={size} fill="#f5a524" color="#f5a524" />
      <span className="text-[11px] font-bold" style={{ color: "#f08a00" }}>{r.toFixed(1)}</span>
      <span className="text-[10px]" style={{ color: M.muted }}>({reviews(id) > 999 ? `${(reviews(id) / 1000).toFixed(1)}k` : reviews(id)})</span>
    </span>
  );
}

/** Section heading: bold title, optional View All link, optional extra controls. */
export function MTitle({ children, to, right, more = "View All", className = "" }) {
  return (
    <div className={`flex items-center justify-between gap-3 flex-wrap ${className}`}>
      <h2 className="text-[20px] md:text-[24px] font-extrabold leading-tight" style={{ color: M.ink }}>{children}</h2>
      <div className="flex items-center gap-3">
        {right}
        {to && <Link to={to} className="mv-viewall inline-flex items-center gap-1 text-[12px] font-bold" style={{ color: M.ink }}>{more} <ChevronRight size={13} className="mv-arrow" /></Link>}
      </div>
    </div>
  );
}

function useAdd(p, s, cart) {
  const hasOptions = needsOptions(p);
  const [added, setAdded] = useState(false);
  const add = (e) => {
    e.preventDefault();
    if (hasOptions) { window.location.href = `/store/${s.slug}/product/${p.id}`; return; }
    cart.add(p, null, 1); toast.success("Added to cart"); setAdded(true); setTimeout(() => setAdded(false), 1300);
  };
  return { hasOptions, added, add };
}

export function Heart_({ className = "" }) {
  const [on, setOn] = useState(false);
  return (
    <button type="button" aria-label="Wishlist" aria-pressed={on} onClick={(e) => { e.preventDefault(); setOn((v) => !v); }}
      className={`w-7 h-7 rounded-full bg-white/90 flex items-center justify-center shadow-sm transition-transform hover:scale-125 ${className}`}>
      <Heart size={14} fill={on ? M.red : "none"} color={on ? "#e11d2e" : "#94a3b8"} className={on ? "mv-heart-pop" : ""} />
    </button>
  );
}

/** Vertical product card: label, heart, photo, name, pack size, stars, price + compare price, green Add to Cart. */
export function MeatCard({ p, s, cart, k = 0, className = "" }) {
  const { slug } = s;
  const { hasOptions, added, add } = useAdd(p, s, cart);
  const soldOut = p.stock <= 0;
  const b = badgeOf(p, k);
  return (
    <div data-testid={`storefront-product-${p.id}`} className={`mv-card group relative h-full flex flex-col bg-white rounded-2xl border p-3 ${className}`} style={{ borderColor: M.line }}>
      <span className="mv-badge absolute top-3.5 left-3.5 z-10 text-[9px] font-extrabold tracking-wide text-white px-2 py-[3px] rounded" style={{ background: b.color }}>{b.label}</span>
      <Heart_ className="absolute top-3 right-3 z-10" />
      <Link to={`/store/${slug}/product/${p.id}`} className="relative block rounded-xl overflow-hidden aspect-[5/4]" style={{ background: M.soft }}>
        {imgOf(p) ? <ToneImg src={imgOf(p)} alt={p.name} loading="lazy" decoding="async" imgClassName="mv-zoom" /> : <Drumstick size={34} className="m-auto mt-[28%]" style={{ color: M.muted }} />}
        {soldOut && <span className="absolute inset-0 flex items-center justify-center text-[11px] font-bold uppercase tracking-wider" style={{ background: "rgba(255,255,255,.75)", color: M.muted }}>Sold out</span>}
      </Link>
      <Link to={`/store/${slug}/product/${p.id}`}><h3 className="mt-3 text-[13px] font-bold leading-snug line-clamp-2 min-h-[2.6em]" style={{ color: M.ink }}>{p.name}</h3></Link>
      <div className="text-[11px] mt-0.5" style={{ color: M.muted }}>{packText(p)}</div>
      <Stars id={p.id} className="mt-1.5" />
      <div className="mt-1.5 flex items-baseline gap-2 flex-wrap">
        <span className="text-[16px] font-extrabold" style={{ color: M.ink }}>{priceText(p)}</span>
        {p.compare_at_price && !hasOptions ? <span className="text-[12px] line-through" style={{ color: "#94a3b8" }}>{inr(p.compare_at_price)}</span> : null}
      </div>
      <button type="button" onClick={add} disabled={soldOut} data-testid={`storefront-add-to-cart-btn-${p.id}`} aria-label={hasOptions ? "Select options" : "Add to cart"}
        className="mv-btn mt-3 h-10 w-full rounded-lg text-[12px] font-bold inline-flex items-center justify-center gap-2 disabled:opacity-50" style={{ background: M.greenSoft, color: M.green }}>
        <ShoppingCart size={14} className="mv-cart-ic" />{soldOut ? "Sold out" : added ? "Added ✓" : hasOptions ? "Select Options" : "Add to Cart"}
      </button>
    </div>
  );
}

/** Compact horizontal card used in the grids (photo, heart, name, pack, stars, price, round cart button). */
export function MeatRow({ p, s, cart, className = "" }) {
  const { slug } = s;
  const { hasOptions, add } = useAdd(p, s, cart);
  return (
    <div data-testid={`storefront-product-row-${p.id}`} className={`mv-card group relative flex gap-3 bg-white rounded-xl border p-2.5 h-full ${className}`} style={{ borderColor: M.line }}>
      <Link to={`/store/${slug}/product/${p.id}`} className="relative shrink-0 w-[72px] h-[72px] md:w-[80px] md:h-[80px] rounded-lg overflow-hidden" style={{ background: M.soft }}>
        {imgOf(p) && <ToneImg src={imgOf(p)} alt={p.name} loading="lazy" imgClassName="mv-zoom" />}
      </Link>
      <div className="min-w-0 flex-1 flex flex-col">
        <div className="flex items-start justify-between gap-2">
          <Link to={`/store/${slug}/product/${p.id}`} className="min-w-0"><h3 className="text-[12px] font-bold leading-snug line-clamp-2" style={{ color: M.ink }}>{p.name}</h3></Link>
          <Heart_ className="!w-6 !h-6 shrink-0 -mt-0.5" />
        </div>
        <div className="text-[10.5px]" style={{ color: M.muted }}>{packText(p)}</div>
        <div className="mt-auto pt-1 flex items-end justify-between gap-2">
          <div className="min-w-0">
            <Stars id={p.id} size={10} />
            <div className="flex items-baseline gap-1.5"><span className="text-[13.5px] font-extrabold" style={{ color: M.ink }}>{priceText(p)}</span>{p.compare_at_price && !hasOptions ? <span className="text-[10.5px] line-through" style={{ color: "#94a3b8" }}>{inr(p.compare_at_price)}</span> : null}</div>
          </div>
          <button type="button" onClick={add} disabled={p.stock <= 0} aria-label={hasOptions ? "Select options" : "Add to cart"} data-testid={`storefront-add-to-cart-btn-${p.id}`}
            className="mv-btn w-8 h-8 shrink-0 rounded-lg inline-flex items-center justify-center disabled:opacity-40" style={{ background: M.greenSoft, color: M.green }}><ShoppingCart size={14} className="mv-cart-ic" /></button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ header */
export function MeatHeader({ s, cart }) {
  const { store, slug, categories = [] } = s;
  const nav = useNavigate();
  const { pathname } = useLocation();
  const [searching, setSearching] = useState(false);
  const [q, setQ] = useState("");
  const [stuck, setStuck] = useState(false);
  const [menu, setMenu] = useState(false);
  const closeT = useRef(null);
  useEffect(() => { const f = () => setStuck(window.scrollY > 40); f(); window.addEventListener("scroll", f, { passive: true }); return () => window.removeEventListener("scroll", f); }, []);
  const search = (e) => { e.preventDefault(); setSearching(false); nav(`/store/${slug}/shop${q ? `?q=${encodeURIComponent(q)}` : ""}`); };
  const links = navLinks(slug);
  const account = links.find(([l]) => l === "My Account")?.[1] || `/store/${slug}/track`;
  const openMenu = () => { clearTimeout(closeT.current); setMenu(true); };
  const closeMenu = () => { closeT.current = setTimeout(() => setMenu(false), 140); };
  // Standard theme header: logo, the standard menu (Shop opens the categories), search, account, cart and the mobile menu.
  return (
    <header className="sticky top-0 z-30 bg-white" style={{ boxShadow: stuck ? "0 8px 26px rgba(11,31,63,.1)" : "none", borderBottom: `1px solid ${M.line}`, transition: "box-shadow .3s" }}>
      <div className="max-w-[1320px] mx-auto px-5 h-[74px] flex items-center justify-between gap-6">
        <Link to={`/store/${slug}`} data-testid="storefront-name" className="shrink-0 flex items-center gap-2.5 truncate max-w-[300px]">
          <StoreLogo store={store} height={46} fallback={<>
            <span className="mv-wiggle w-11 h-11 rounded-full flex items-center justify-center text-white shadow-md" style={{ background: M.red }}><Drumstick size={22} /></span>
            <span className="min-w-0"><span className="block text-[20px] font-extrabold leading-none truncate" style={{ color: M.ink }}>{store.name}</span>
              <span className="block text-[8px] font-bold tracking-[.18em] uppercase mt-1 truncate" style={{ color: M.muted }}>{store.business_category || "Fresh & Premium"}</span></span></>} />
        </Link>
        <nav className="hidden lg:flex items-center gap-8 text-[13px] font-semibold" data-testid="storefront-nav">
          {links.filter(([l]) => l !== "My Account").map(([l, to]) => { const on = isNavActive(pathname, slug, to); const shop = l === "Shop"; return (
            <div key={l} className="relative" onMouseEnter={shop ? openMenu : undefined} onMouseLeave={shop ? closeMenu : undefined}>
              <Link to={to} aria-current={on ? "page" : undefined} className="relative inline-flex items-center gap-1 py-1 transition-colors hover:opacity-80" style={{ color: on ? M.red : M.ink }} data-testid={l === "Track Order" ? "storefront-track-link" : undefined}>
                {l}{shop && categories.length > 0 && <ChevronDown size={13} className={`transition-transform ${menu ? "rotate-180" : ""}`} />}
                <span className="absolute left-0 right-0 -bottom-1 h-[2px] rounded origin-left transition-transform duration-300" style={{ background: M.red, transform: on ? "scaleX(1)" : "scaleX(0)" }} />
              </Link>
              {shop && menu && categories.length > 0 && (
                <div className="mv-drop absolute left-0 top-full pt-3 z-40" onMouseEnter={openMenu} onMouseLeave={closeMenu} data-testid="meat-shop-menu">
                  <div className="rounded-xl bg-white border shadow-2xl py-2 min-w-[200px]" style={{ borderColor: M.line }}>
                    {categories.map((c) => <Link key={c} to={`/store/${slug}/shop?category=${encodeURIComponent(c)}`} className="block px-4 py-2 text-[13px] font-medium hover:bg-[#f4f7fb]" style={{ color: M.ink }} onClick={() => setMenu(false)}>{c}</Link>)}
                    <Link to={`/store/${slug}/shop`} className="block px-4 py-2 text-[13px] font-bold border-t mt-1" style={{ color: M.red, borderColor: M.line }} onClick={() => setMenu(false)}>All products →</Link>
                  </div>
                </div>
              )}
            </div>
          ); })}
        </nav>
        <div className="flex items-center gap-4" style={{ color: M.ink }}>
          <button onClick={() => setSearching((x) => !x)} aria-label="Search" data-testid="meat-search-toggle" className="hover:opacity-60 transition-opacity"><Search size={19} /></button>
          <Link to={account} aria-label="My account" className="hidden sm:block hover:opacity-60 transition-opacity"><User size={19} /></Link>
          <button onClick={() => cart.setOpen(true)} data-testid="storefront-cart-btn" aria-label="Open cart" className="relative hover:opacity-70 transition-opacity">
            <ShoppingCart size={21} />
            <span key={cart.count} className="mv-pop absolute -top-2 -right-3 min-w-[17px] h-[17px] px-1 rounded-full text-[10px] font-bold text-white flex items-center justify-center" style={{ background: M.red }}>{cart.count}</span>
          </button>
          <MobileNav slug={slug} color={M.ink} bg="#fff" accent={M.red} border={M.line} />
        </div>
      </div>
      {searching && (
        <form onSubmit={search} className="border-t rx-fade" style={{ borderColor: M.line, background: M.soft }}>
          <div className="max-w-[1320px] mx-auto px-5 h-14 flex items-center gap-3">
            <Search size={16} style={{ color: M.muted }} />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search fresh products…" aria-label="Search products" data-testid="meat-search" className="flex-1 outline-none text-[14px] bg-transparent" style={{ color: M.ink }} />
            <button type="button" onClick={() => setSearching(false)} aria-label="Close search"><X size={16} /></button>
          </div>
        </form>
      )}
    </header>
  );
}

/* ------------------------------------------------------------------ footer */
const PROMISES = [[Sparkles, "100% Fresh Guaranteed", "Sourced & delivered fresh"], [ShieldCheck, "Hygienic Processing", "Clean, safe and premium quality"], [Truck, "Quick & Reliable Delivery", "On time, every time"], [RotateCcw, "Easy Returns", "Hassle-free support"]];

export function MeatFooter({ s }) {
  const { store, slug } = s;
  const [top, setTop] = useState(false);
  useEffect(() => { const f = () => setTop(window.scrollY > 600); window.addEventListener("scroll", f, { passive: true }); return () => window.removeEventListener("scroll", f); }, []);
  const lines = footerLines(store);
  // Same structure as every theme: trust strip, store details + the three standard link columns, copyright.
  return (
    <footer data-testid="storefront-footer" style={{ background: M.navy, color: "#b9c6dd" }}>
      <div style={{ borderBottom: "1px solid rgba(255,255,255,.1)" }}>
        <div className="max-w-[1320px] mx-auto px-5 py-7 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {PROMISES.map(([I, h, d], k) => (
            <Reveal key={h} delay={k * 90} className="mv-promise flex gap-3.5 items-center">
              <span className="mv-promise-ic w-12 h-12 rounded-full flex items-center justify-center shrink-0" style={{ background: "rgba(255,255,255,.08)", color: "#ff8a95" }}><I size={22} /></span>
              <div><div className="text-[14px] font-bold text-white">{h}</div><p className="text-[11.5px] mt-0.5" style={{ color: "#8fa3c4" }}>{d}</p></div>
            </Reveal>
          ))}
        </div>
      </div>
      <div className="max-w-[1320px] mx-auto px-5 py-12 grid md:grid-cols-5 gap-9">
        <Reveal className="md:col-span-2">
          <StoreLogo store={store} height={48} fallback={<div className="flex items-center gap-2.5"><span className="w-11 h-11 rounded-full flex items-center justify-center text-white" style={{ background: M.red }}><Drumstick size={22} /></span><span className="text-[22px] font-extrabold text-white">{store.name}</span></div>} />
          <p className="text-[13px] mt-4 flex gap-3"><MapPin size={16} className="mt-0.5 shrink-0" style={{ color: "#ff8a95" }} />{lines.location}</p>
          <p className="text-[13px] mt-2 flex gap-3"><Mail size={16} className="mt-0.5 shrink-0" style={{ color: "#ff8a95" }} />{lines.support}</p>
          <div className="mt-4 flex gap-2.5">{socialItems(s).map((I) => <a key={I.network} href={I.url} target="_blank" rel="noopener noreferrer" aria-label={I.label} title={I.label} data-testid={`footer-social-${I.network}`}
            className="w-9 h-9 rounded-full flex items-center justify-center border transition-all hover:bg-white/10 hover:-translate-y-1" style={{ borderColor: "rgba(255,255,255,.25)", color: "#fff" }}><I.icon size={14} /></a>)}</div>
        </Reveal>
        {footerColumns(slug).map(([h, links], i) => (
          <Reveal key={h} delay={(i + 1) * 90}>
            <div className="text-[13px] font-extrabold text-white pb-3 border-b" style={{ borderColor: "rgba(255,255,255,.14)" }}>{h}</div>
            <ul className="mt-5 space-y-3 text-[13px]">{links.map(([l, to]) => <li key={l}><Link to={to} className="rx-link hover:text-white">{l}</Link></li>)}</ul>
          </Reveal>
        ))}
      </div>
      <div className="border-t text-center text-[12px] py-5" style={{ borderColor: "rgba(255,255,255,.12)", color: "#8fa3c4" }}>© 2026 {store.name} · Powered by <Link to="/" className="underline">Sell24x7</Link></div>
      {top && <button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} aria-label="Back to top" className="fixed bottom-6 right-6 z-30 w-11 h-11 rounded-full flex items-center justify-center text-white shadow-lg rx-slide-up hover:-translate-y-1 transition-transform" style={{ background: M.red }}><ChevronUp size={18} /></button>}
    </footer>
  );
}

export { Snowflake };
